<template>
  <div class="custom-select-wrapper" :class="{ 'has-value': hasValue, 'is-open': isOpen }">
    <select
      :id="id"
      :data-test="dataTest"
      v-model="selectedValue"
      class="custom-select"
      :required="required"
      @focus="isOpen = true"
      @blur="isOpen = false"
      @click="isOpen = !isOpen"
    >
      <option value="" disabled>{{ placeholder }}</option>
      <option v-for="(option, index) in options" :key="index" :value="getOptionValue(option)">
        {{ getOptionLabel(option) }}
      </option>
    </select>
    <span v-if="!hasValue && required" class="required-text">
      {{ requiredMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

interface Props {
  id: string
  modelValue: unknown
  options: unknown[]
  optionValue?: string | ((option: unknown) => unknown)
  optionLabel?: string | ((option: unknown) => string)
  placeholder?: string
  required?: boolean
  requiredMessage?: string
  uppercase?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Selecciona una opción',
  required: false,
  requiredMessage: 'Es necesario seleccionar una opción para continuar',
  optionValue: 'value',
  optionLabel: 'label',
})

const emit = defineEmits<{
  'update:modelValue': [value: unknown]
}>()

const isOpen = ref(false)

const selectedValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const dataTest = computed(() => {
  return props.id ? `test-${props.id}` : undefined
})

const hasValue = computed(() => {
  return props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== ''
})

const getOptionValue = (option: unknown) => {
  if (typeof props.optionValue === 'function') {
    return props.optionValue(option)
  }
  return (option as Record<string, unknown>)[props.optionValue as string]
}

const getOptionLabel = (option: unknown) => {
  if (typeof props.optionLabel === 'function') {
    return props.optionLabel(option)
  }
  return (option as Record<string, unknown>)[props.optionLabel as string]
}
</script>

<style scoped>
.custom-select-wrapper {
  position: relative;
  width: 100%;
}

.custom-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 100%;
  height: 48px;
  background: #f2f2f2;
  border-radius: 16px;
  border: none;
  padding: 0 40px 0 16px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #a1a1a1;
  outline: none;
  display: flex;
  align-items: center;
}

.custom-select option:disabled {
  color: #a1a1a1;
}

.custom-select option:not(:disabled) {
  color: #2b2a2a;
  background: #ffffff;
}

/* Separator line */
.custom-select-wrapper::after {
  content: '';
  position: absolute;
  width: 2px;
  height: 36px;
  right: 40px;
  top: calc(50% - 36px / 2);
  background: #6b6b6b;
  pointer-events: none;
  z-index: 2;
}

/* Arrow */
.custom-select-wrapper::before {
  content: '';
  position: absolute;
  width: 24px;
  height: 24px;
  right: 8px;
  top: calc(50% - 24px / 2);
  background: #6b6b6b;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")
    no-repeat center;
  mask-size: 42px 42px;
  pointer-events: none;
  z-index: 3;
  transition: transform 0.3s ease;
}

/* Arrow rotation when open */
.custom-select-wrapper.is-open::before {
  transform: rotate(180deg);
}

.required-text {
  color: #d32f2f;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  display: block;
}
</style>
